import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterPanel, type FilterField } from '@/components/common/AdvancedFilter';
import {
  bookEditionsQueryOptions,
  bookTypesQueryOptions,
} from '@/features/product/api/product.api';
import { instituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useOrgGeoFilterOptions } from '@/hooks/useOrgGeoFilterOptions';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import {
  getDateFromToFilterFields,
  getGeoFilterFields,
  getInstituteTypeFilterField,
  getZonalManagerFilterField,
} from '@/utils/filterFields';

const LOOKUP_PARAMS = { page: 1, pageSize: 200, isActive: true } as const;

interface RequisitionFilterPanelProps {
  filters: UrlSyncedFilters;
  pageFields?: FilterField[];
  withDateRange?: boolean;
  withMember?: boolean;
  withGeo?: boolean;
  withInstituteType?: boolean;
  withBook?: boolean;
}

export function RequisitionFilterPanel({
  filters,
  pageFields = [],
  withDateRange = false,
  withMember = false,
  withGeo = false,
  withInstituteType = false,
  withBook = false,
}: RequisitionFilterPanelProps) {
  const { filterValues } = filters;

  const { data: zonalManagers } = useQuery({
    ...zonalManagersQueryOptions(),
    enabled: withMember,
  });
  const { data: instituteTypes } = useQuery({
    ...instituteTypesQueryOptions(),
    enabled: withInstituteType,
  });
  const { data: bookTypes } = useQuery({
    ...bookTypesQueryOptions(LOOKUP_PARAMS),
    enabled: withBook,
  });
  const { data: editions } = useQuery({
    ...bookEditionsQueryOptions(LOOKUP_PARAMS),
    enabled: withBook,
  });
  const orgGeoOptions = useOrgGeoFilterOptions(filterValues);

  const fields: FilterField[] = [
    ...(withDateRange ? getDateFromToFilterFields() : []),
    ...(withMember ? [getZonalManagerFilterField(zonalManagers)] : []),
    ...(withGeo ? getGeoFilterFields(filterValues, orgGeoOptions) : []),
    ...(withInstituteType ? [getInstituteTypeFilterField(instituteTypes)] : []),
    ...pageFields,
    ...(withBook
      ? [
          {
            type: 'select' as const,
            key: 'bookTypeId',
            label: 'Book Type',
            options: (bookTypes?.data ?? []).map((type) => ({
              value: String(type.id),
              label: type.name,
            })),
          },
          {
            type: 'select' as const,
            key: 'bookEditionId',
            label: 'Book Edition',
            options: (editions?.data ?? []).map((edition) => ({
              value: String(edition.id),
              label: edition.name,
            })),
          },
        ]
      : []),
  ];

  return <AdvancedFilterPanel fields={fields} {...getFilterPanelProps(filters)} />;
}
